/**
 * A faixa que avisa o parceiro que o atendente está chamando.
 *
 * **Por que existe (07/08/2026).** Não havia NENHUMA forma de o parceiro saber.
 * O atendente clicava "Iniciar chamada", via um cronômetro correndo, e do outro
 * lado não acontecia nada — nem faixa, nem som, nem aviso. A chamada existia
 * só na cabeça de quem ligou.
 *
 * **Enquanto a conversa estiver aberta.** Foi a decisão do Augusto entre as
 * opções: a faixa reaproveita o polling que já roda na tela, e não depende das
 * chaves VAPID de push, que ainda não estão definitivas. Com a aba fechada o
 * parceiro não é alcançado — mas hoje ele não é alcançado de jeito nenhum, e
 * "tocar mesmo fora da página" é um passo seguinte, não um pré-requisito.
 *
 * **Consentimento antes de entrar.** Se o parceiro ainda não aceitou a
 * gravação, o botão abre o modal de aceite em vez de tentar entrar e tomar 412.
 * Tratar o 412 como erro na cara do usuário seria transformar uma exigência
 * legal prevista em mensagem de falha.
 */
import { PhoneCall } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { ChamadaEmAndamento } from "@/types/atendimento";

export interface FaixaDeChamadaProps {
  chamada: ChamadaEmAndamento | undefined;
  /** Entrar na chamada (já com consentimento registrado). */
  onEntrar: () => void;
  /** Abrir o modal de consentimento — quando ele ainda não foi dado. */
  onPedirConsentimento: () => void;
  /** Verdadeiro enquanto o token está sendo pedido. */
  entrando?: boolean;
  /** Esconde a faixa quando o parceiro JÁ está na chamada. */
  jaNaChamada?: boolean;
}

export function FaixaDeChamada({
  chamada,
  onEntrar,
  onPedirConsentimento,
  entrando = false,
  jaNaChamada = false,
}: FaixaDeChamadaProps) {
  if (!chamada?.ativa || jaNaChamada) return null;

  const quem = chamada.atendente_primeiro_nome;
  const precisaConsentir = !chamada.consentimento_registrado;

  return (
    <div
      className="flex items-center gap-3 border-b border-emerald-200 bg-emerald-50 px-3 py-2"
      role="status"
      data-testid="portal-faixa-chamada"
    >
      <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
        <PhoneCall className="h-4 w-4" aria-hidden="true" />
        {/* Pulso: numa faixa estática o parceiro pode não perceber que algo
            mudou na tela enquanto lê as mensagens. */}
        <span className="absolute inset-0 animate-ping rounded-full bg-emerald-500/40" />
      </span>

      <div className="min-w-0 flex-1 text-sm">
        <div className="font-medium text-emerald-900">
          {quem ? `${quem} quer falar com você` : "Chamada em andamento"}
        </div>
        {precisaConsentir && (
          <div className="text-xs text-emerald-800">
            A chamada é gravada — você precisa concordar para entrar.
          </div>
        )}
      </div>

      <Button
        type="button"
        size="sm"
        onClick={precisaConsentir ? onPedirConsentimento : onEntrar}
        disabled={entrando}
        data-testid="portal-entrar-chamada"
      >
        {entrando ? "Entrando…" : "Entrar"}
      </Button>
    </div>
  );
}
